<script setup lang="ts">
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import useCpBem from '~/composables/useCpBem';
import CpBadgeWarrantyModule from '~/components/cp/CpBadgeWarrantyModule/CpBadgeWarrantyModule.vue';
import CpModalWarrantyModule from '~/components/cp/CpModal/CpModalWarrantyModule/CpModalWarrantyModule.vue';
import CpText from '~/components/cp/CpText/CpText.vue';
import CpLink from '~/components/cp/CpLink/CpLink.vue';

const {b, e, em} = useCpBem('cp-section-pdp-warranty-module');
const {t} = useI18n();

enum WarrantyKind {
    Brand = 'Brand',
    FolioRequired = 'FolioRequired',
    Cp = 'Cp',
    Provider = 'Provider'
}

const props = defineProps({
    brandName: {
        type: String,
        required: false,
        default: 'Cyberpuerta',
    },
    kind: {
        required: true,
        type: String,
        validator: (value:string)=>['Brand', 'FolioRequired', 'Cp', 'Provider'].includes(value),
    },
    warrantyTime: {
        required: false,
        type: Number,
        default: 1,
    },
    title: {
        required: false,
        type: String,
        default: '',
    },
    href: {
        required: false,
        type: String,
        default: 'https://www.cyberpuerta.mx/garantias-y-devoluciones/',
    },
    showLink: {
        required: false,
        type: Boolean,
        default: true,
    },
});

const displayModal = ref(false);

const badgeSelector = computed(()=>{
    switch (props.kind) {
        case WarrantyKind.Brand:
            return 'Brand';
        case WarrantyKind.FolioRequired:
            return 'ProviderBrandFolioRequired';
        case WarrantyKind.Cp:
            return 'Cp';
        case WarrantyKind.Provider:
            return 'ProviderBrandFolioRequired';
        default:
            return 'Brand';
    }
});
const headerText = computed(()=>{
    switch (props.kind) {
        case WarrantyKind.Brand:
            return t('brandBody', {brandName: props.brandName, count: props.warrantyTime});
        case WarrantyKind.FolioRequired:
            return t('folioRequiredBody', {count: props.warrantyTime});
        case WarrantyKind.Cp:
            return t('cpBody', {count: props.warrantyTime});
        case WarrantyKind.Provider:
            return t('providerBody', {brandName: props.brandName, count: props.warrantyTime});
        default:
            return t('brandBody', {brandName: props.brandName, count: props.warrantyTime});
    }
});
</script>

<template>
    <div :class="b">
        <div :class="e('container')">
            <div :class="e('badge')">
                <CpBadgeWarrantyModule
                    :kind="badgeSelector"
                    :brand-name="brandName"
                    :title="title"
                />
            </div>
            <div :class="e('header')">
                <CpText variant="subheading">
                    {{ headerText }}
                </CpText>
            </div>

            <div v-if="showLink" :class="e('link')">
                <CpLink :new-window="true" @click="displayModal = true">
                    {{ t('knowMore') }}
                </CpLink>
            </div>
        </div>

        <CpModalWarrantyModule
            v-bind="{
                show: displayModal,
                brandName,
                kind,
                warrantyTime,
                hrefFirstButton: href,
                persistent: false,
            }"
            @update:show="displayModal = false"
        />
    </div>
</template>
<style lang="scss" scoped>

.cp-section-pdp-warranty-module {
    &__container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        width: 100%;
        min-width: 360px;
        padding: 16px;
        border-top: 1px solid var(--color-neutral-extra-light);
    }

    &__badge {
        display: flex;
        justify-content: center;
        gap: 12px;
    }
    &__header {
        width: 90%;
        display: flex;
        justify-content: center;
        gap: 12px;
        text-align: center;
        font-weight: 600;
        line-height: 16px;
        text-wrap: balance;
    }
    &__link {
        display: flex;
        justify-content: center;
    }
}
</style>
<i18n locale="es-US" lang="yaml">
    brandBody: "¡Tu producto tiene garantía con {brandName} por {count} año! | ¡Tu producto tiene garantía con {brandName} por {count} años!"
    folioRequiredBody: "¡Tu producto tiene garantía por {count} año! | ¡Tu producto tiene garantía por {count} años!"
    cpBody: "¡La garantía de tu producto está a cargo de Cyberpuerta por {count} año! | ¡La garantía de tu producto está a cargo de Cyberpuerta por {count} años!"
    providerBody: "¡Tu producto tiene garantía con un Centro de Servicio Especializado en {brandName} por {count} año! | ¡Tu producto tiene garantía con un Centro de Servicio Especializado en {brandName} por {count} años!"
    knowMore: "Conocer más acerca de esta garantía"
</i18n>
